import { useEffect, useState } from "react";
import { SortingArrowsIcon } from "../../atoms/icons/SortingArrowsIcon";
import { theme } from "../../../theme";
import Checkbox from "../../atoms/inputs/Checkbox";
import BodySmall from "../../atoms/typography/BodySmall";
import { Col } from "react-grid-system";
import TableHeader from "../../atoms/TableHeader";
import TableRow from "../../atoms/TableRow";
import TableHeaderText from "../../atoms/typography/TableHeaderText";
import { ChevronSmallRightIcon } from "../../atoms/icons/ChevronIcons";
import dayjs from "dayjs";
import { formatPrice, getCurrencySymbol } from "../../../utils/formatters";
import { useTranslation } from "react-i18next";
import Tooltip from "../Tooltip";
import { useNavigate, useLocation } from "react-router-dom";
import {
  InvoiceAssessmentRoute,
  InvoicesListRoute,
  InvoiceValidationRoute,
} from "../../../utils/routes";
import {
  getInvoiceListStatusBadge,
  getInvoiceTagTitleKey,
} from "../../../utils/invoicesHelpers";
import type { InvoiceTag } from "../../../models/InvoiceTags";
import TextTag from "../../atoms/TextTag";
import { useClientInvoices } from "../../../graphql/hooks/invoices/useClientInvoices";
import CardBody from "../../atoms/CardBody";
import type { SortDirection } from "../../../models/SortDirection";
import SmallLoadingSpinner from "../../atoms/SmallLoadingSpinner";
import Alert from "../Alert";
import PaginationComponent from "../PaginationComponent";
import type {
  InvoiceAssessmentStatus,
  InvoiceProcessStatus,
  InvoiceValidationStatus,
} from "../../../models/InvoiceStatusModel";
import NoContentView from "../NoContentView";
import Modal from "../Modal";
import DuplicatedInvoiceNumberListModalContent from "../modals/invoices/DuplicatedInvoiceNumberListModalContent";
import styled from "styled-components";
import type { ClientFilterType } from "../../../graphql/hooks/invoices/useClientAssessmentInvoices";

interface Props {
  selectedInvoices: string[];
  selectedTab:
    | "all"
    | "underAssessment"
    | "underValidation"
    | "rejected"
    | "done"
    | "autopilotPause";
  setSelectedInvoices: (invoices: string[]) => void;
  clientIdentifier: string;
  invoiceDateFrom?: Date;
  invoiceDateTo?: Date;
  dueDateFrom?: Date;
  dueDateTo?: Date;
  invoiceStatuses?: Array<InvoiceProcessStatus>;
  validationStatuses?: InvoiceValidationStatus[];
  assessmentStatuses?: InvoiceAssessmentStatus[];
  amountFrom?: number;
  amountTo?: number;
  selectedTags?: InvoiceTag[];
  onInvoicesLoaded?: (
    items: Array<{ id: string; tags?: Array<{ tag: InvoiceTag }> }>,
  ) => void;
  clientFilterType?: ClientFilterType;
}

const ITEMS_PER_PAGE = 20;

const DupeButtonContainer = styled.button`
  border: none;
  background-color: transparent;
  padding: 0;
  margin: 0;
  cursor: pointer;

  &:hover {
    filter: opacity(0.6);
  }
`;

const InvoiceListOverviewItem: React.FC<Props> = ({
  selectedInvoices,
  setSelectedInvoices,
  clientIdentifier,
  invoiceDateFrom,
  invoiceDateTo,
  dueDateFrom,
  dueDateTo,
  invoiceStatuses,
  validationStatuses,
  assessmentStatuses,
  amountFrom,
  amountTo,
  selectedTags,
  onInvoicesLoaded,
  clientFilterType,
  selectedTab,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [sortDirection, setSortDirection] = useState<SortDirection>("DESC");

  const [selectedDupeInvoiceNumber, setSelectedDupeInvoiceNumber] = useState<
    string | undefined
  >(undefined);
  const [dupeInvoiceModalOpen, setDupeInvoiceModalOpen] = useState(false);

  const { invoices, error, loading } = useClientInvoices(clientIdentifier, {
    sortBy: "CREATED_AT",
    sortDirection,
    invoiceDateFrom,
    invoiceDateTo,
    dueDateFrom,
    dueDateTo,
    invoiceStatuses,
    validationStatuses,
    assessmentStatuses,
    amountFrom,
    amountTo,
    tags: selectedTags,
    skip: currentPageIndex * ITEMS_PER_PAGE,
    take: ITEMS_PER_PAGE,
    clientFilterType,
  });
  useEffect(() => {
    if (invoices?.items) {
      onInvoicesLoaded?.(invoices.items);
    }
  }, [invoices?.items]);

  //invoice selection
  const allInvoiceIds = invoices?.items?.map((invoice) => invoice.id) ?? [];
  const selectedCount = allInvoiceIds.filter((id) =>
    selectedInvoices.includes(id),
  ).length;
  const allInvoiceIdsAreSelected =
    allInvoiceIds.length > 0 && selectedCount === allInvoiceIds.length;
  const someInvoicesAreSelected =
    selectedCount > 0 && !allInvoiceIdsAreSelected;
  const toggleSelectAll = () => {
    if (allInvoiceIdsAreSelected) {
      setSelectedInvoices(
        selectedInvoices.filter((id) => !allInvoiceIds.includes(id)),
      );
    } else {
      setSelectedInvoices(
        Array.from(new Set([...selectedInvoices, ...allInvoiceIds])),
      );
    }
  };

  const onSelectInvoice = (invoiceId: string) => {
    if (selectedInvoices.includes(invoiceId)) {
      setSelectedInvoices(selectedInvoices.filter((id) => id !== invoiceId));
    } else {
      setSelectedInvoices([...selectedInvoices, invoiceId]);
    }
  };

  const onClickGoToInvoice = (
    invoiceId: string,
    status: InvoiceProcessStatus,
  ) => {
    navigate(location.pathname + location.search, {
      replace: true,
      state: { ...location.state, expandedClientId: clientIdentifier },
    });
    if (status === "VALIDATION") {
      navigate(`/invoices/validate/${invoiceId}/${clientIdentifier}`, {
        state: { from: InvoiceValidationRoute.id },
      });
    } else if (status === "ASSESSMENT") {
      navigate(`/invoices/assess/${clientIdentifier}/${invoiceId}`, {
        state: {
          from: InvoiceAssessmentRoute.id,
        },
      });
    } else {
      navigate(`/invoices/overview/${invoiceId}/${clientIdentifier}`, {
        state: {
          from: InvoicesListRoute.id,
        },
      });
    }
  };

  const numberOfPages = invoices
    ? Math.ceil(invoices.totalCount / ITEMS_PER_PAGE)
    : 0;

  const onClickOpenDupeInvoiceModal = (invoiceNumber: string) => {
    setSelectedDupeInvoiceNumber(invoiceNumber);
    setDupeInvoiceModalOpen(true);
  };

  return (
    <div style={{ borderBottom: `1px solid ${theme.colors.grey05}` }}>
      {loading && (
        <CardBody>
          <SmallLoadingSpinner />
        </CardBody>
      )}
      {error && !invoices?.items && (
        <CardBody>
          <Alert
            type="error"
            title="Kunde inte hämta fakturor"
            message={error?.toString()}
          />
        </CardBody>
      )}
      {!loading && (!invoices?.items || invoices.items.length === 0) && (
        <CardBody>
          <NoContentView />
        </CardBody>
      )}
      {!loading && invoices?.items && invoices.items.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <div style={{ minWidth: 980 }}>
            <TableHeader align="center" nogutter>
              <div
                style={{
                  width: 48,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Checkbox
                  checked={allInvoiceIdsAreSelected}
                  indeterminate={someInvoicesAreSelected}
                  onChange={toggleSelectAll}
                />
              </div>
              <Col lg={1.6}>
                <TableHeaderText>{t("invoices.invoice")}</TableHeaderText>
              </Col>
              <Col lg={1.4}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    cursor: "pointer",
                  }}
                  onClick={() =>
                    setSortDirection(sortDirection === "ASC" ? "DESC" : "ASC")
                  }
                >
                  <TableHeaderText>
                    {t("documentHandler.registered")}
                  </TableHeaderText>
                  <SortingArrowsIcon color={theme.colors.primary} />
                </div>
              </Col>
              <Col lg={1.4}>
                <TableHeaderText>{t("invoices.latestEdited")}</TableHeaderText>
              </Col>
              <Col lg={2}>
                <TableHeaderText>
                  {clientFilterType === "RECIPIENT"
                    ? t("invoices.issuer")
                    : t("common.recipient")}
                </TableHeaderText>
              </Col>
              <Col lg={1.6}>
                <TableHeaderText>{t("invoices.invoiceDate")}</TableHeaderText>
              </Col>
              <Col lg={1.4}>
                <TableHeaderText>{t("invoices.amount")}</TableHeaderText>
              </Col>
              <Col lg={1.6}>
                <TableHeaderText>
                  {selectedTab === "underAssessment"
                    ? t("invoices.assessmentStatus")
                    : selectedTab === "underValidation"
                      ? t("invoices.validationStatus")
                      : t("common.status")}
                </TableHeaderText>
              </Col>
              <Col lg={0.4}></Col>
            </TableHeader>
            {invoices?.items?.map((invoice) => {
              const processStatus = invoice?.state?.processStatus ?? "UNKNOWN";
              return (
                <TableRow
                  nogutter
                  align="center"
                  key={invoice.id}
                  clickable={true}
                  onClick={() => onClickGoToInvoice(invoice.id, processStatus)}
                >
                  <div
                    style={{
                      width: 48,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Checkbox
                      checked={selectedInvoices.includes(invoice.id)}
                      onChange={() => onSelectInvoice(invoice.id)}
                    />
                  </div>
                  <Col lg={1.6}>
                    <div
                      style={{ display: "flex", gap: 8, alignItems: "center" }}
                    >
                      <BodySmall>{invoice?.invoiceNumber}</BodySmall>
                      {invoice?.hasDuplicateInvoices && (
                        <DupeButtonContainer
                          onClick={(
                            event: React.MouseEvent<HTMLButtonElement>,
                          ) => {
                            event.stopPropagation();
                            onClickOpenDupeInvoiceModal(invoice.id);
                          }}
                        >
                          <TextTag tagTitleKey="DUPE" variant="warning" />
                        </DupeButtonContainer>
                      )}
                      {invoice?.isCreditInvoice && (
                        <TextTag
                          tagTitleKey="invoices.creditInvoice"
                          variant="pink"
                          small
                        />
                      )}
                      {invoice?.state?.registrationStatus === "MIGRATED" && (
                        <TextTag tagTitleKey="Migrerad" variant="green" small />
                      )}
                    </div>
                  </Col>
                  <Col lg={1.4}>
                    <Tooltip
                      text={dayjs(invoice.createdAt).format("YYYY-MM-DD HH:mm")}
                    >
                      <BodySmall>
                        {dayjs(invoice.createdAt)
                          .locale(i18n.language)
                          .fromNow()
                          .replace(/^för /, "")}
                      </BodySmall>
                    </Tooltip>
                  </Col>
                  <Col lg={1.4}>
                    <Tooltip
                      text={dayjs(invoice.modifiedAt).format(
                        "YYYY-MM-DD HH:mm",
                      )}
                    >
                      <BodySmall>
                        {dayjs(invoice.modifiedAt)
                          .locale(i18n.language)
                          .fromNow()
                          .replace(/^för /, "")}
                      </BodySmall>
                    </Tooltip>
                  </Col>
                  <Col lg={2}>
                    <BodySmall>
                      {clientFilterType === "RECIPIENT"
                        ? invoice?.invoiceIssuer?.client?.name
                        : invoice.invoiceRecipient?.name}
                    </BodySmall>
                  </Col>
                  <Col lg={1.6}>
                    <BodySmall>
                      {dayjs(invoice.invoiceDate).format("YYYY-MM-DD")}
                    </BodySmall>
                  </Col>
                  <Col lg={1.4}>
                    <BodySmall>
                      {formatPrice(invoice?.totalAmountWithVat ?? 0)}{" "}
                      {getCurrencySymbol(invoice?.currencyCode) ?? ""}
                    </BodySmall>
                  </Col>
                  <Col
                    lg={1.6}
                    style={{ display: "flex", flexDirection: "column", gap: 4 }}
                  >
                    {invoice.tags?.map((tag) => (
                      <TextTag
                        key={tag.invoiceTagId}
                        tagTitleKey={getInvoiceTagTitleKey(tag.tag)}
                      />
                    ))}
                    {getInvoiceListStatusBadge(
                      invoice?.state?.processStatus ?? "UNKNOWN",
                      true,
                      invoice?.state?.validationStatus,
                      invoice?.state?.assessmentStatus,
                      selectedTab === "underAssessment" ||
                        selectedTab === "underValidation",
                    )}
                    {invoice.state?.processStatus === "REGISTRATION" &&
                      invoice.state.registrationStatus ===
                        "AUTOPILOT_PAUSE" && (
                        <TextTag tagTitleKey="Ej överlåten" variant="info" />
                      )}
                  </Col>
                  <Col
                    lg={0.4}
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "center",
                    }}
                  >
                    {<ChevronSmallRightIcon />}
                  </Col>
                </TableRow>
              );
            })}
          </div>
        </div>
      )}

      <PaginationComponent
        currentPageIndex={currentPageIndex}
        numberOfPages={numberOfPages}
        setCurrentPageIndex={setCurrentPageIndex}
        spacing={1}
      />

      {dupeInvoiceModalOpen && selectedDupeInvoiceNumber && (
        <Modal
          onClose={() => {
            setSelectedDupeInvoiceNumber(undefined);
            setDupeInvoiceModalOpen(false);
          }}
          closeIcon
          width={1250}
        >
          <DuplicatedInvoiceNumberListModalContent
            clientIdentifier={clientIdentifier}
            invoiceId={selectedDupeInvoiceNumber}
          />
        </Modal>
      )}
    </div>
  );
};

export default InvoiceListOverviewItem;
